import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { LogOut, Settings, Truck, UserCog, Fuel, Wrench, Route, ScanLine, Wallet, FileText, Container, Banknote, Satellite, BarChart3, AlertTriangle, HandCoins, Users, ClipboardCheck } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useAuth } from '@/lib/auth';
import { apiClient } from '@/lib/api';
import DriverSelfView from '@/pages/self-view/DriverSelfView';
import InvestorSelfView from '@/pages/self-view/InvestorSelfView';
import { ModuleGroup, type ModuleDef } from '@/components/dashboard/ModuleGroup';
import { MaintenanceCounter } from '@/components/MaintenanceCounter';
import SupervisorHomePage from '@/pages/construction/SupervisorHomePage';
import { useCurrentProject } from '@/contexts/CurrentProjectContext';

// PR-A1/A2 — simplified daily dashboard. Each project shows ONLY its daily
// operational cards + one clean summary. Everything else lives in Settings.

const COMMON_FLEET: ModuleDef[] = [
  { key: 'vehicles',    icon: Truck,   color: 'text-green-500',  route: '/dashboard/vehicles' },
  { key: 'drivers',     icon: UserCog, color: 'text-purple-500', route: '/dashboard/drivers' },
  { key: 'fuel',        icon: Fuel,    color: 'text-orange-500', route: '/dashboard/fuel' },
  { key: 'maintenance', icon: Wrench,  color: 'text-stone-500',  route: '/dashboard/maintenance' },
];

const NEOM_OPS: ModuleDef[] = [
  { key: 'trips',           icon: Route,     color: 'text-teal-500',    route: '/dashboard/trips' },
  { key: 'ocr_review',      icon: ScanLine,  color: 'text-amber-500',   route: '/dashboard/ocr-review' },
  { key: 'payments',        icon: Wallet,    color: 'text-emerald-500', route: '/dashboard/payments' },
  { key: 'driver_advances', icon: HandCoins, color: 'text-indigo-500',  route: '/dashboard/cashflow/advances' },
  { key: 'neom_driver_pay', icon: Wallet,    color: 'text-sky-500',     route: '/dashboard/neom/driver-pay' },
  // Inspections live inside maintenance now - one place for what a truck
  // needs and what was done to it.
  { key: 'maintenance',     icon: Wrench,     color: 'text-lime-500',    route: '/dashboard/maintenance' },
];

const DUMPERS_OPS: ModuleDef[] = [
  // Clients first: a load cannot be logged until a client with a rate exists,
  // and this card was previously missing so the page was unreachable.
  { key: 'dumper_clients',     icon: Users,     color: 'text-sky-500',     route: '/dashboard/dumpers/clients' },
  { key: 'dumper_log',         icon: FileText,  color: 'text-amber-500',   route: '/dashboard/dumpers/log' },
  { key: 'dumper_loads',       icon: Container, color: 'text-amber-500',   route: '/dashboard/dumpers/loads' },
  { key: 'dumper_receivables', icon: Banknote,  color: 'text-emerald-500', route: '/dashboard/dumpers/receivables' },
  { key: 'dumper_trucks',      icon: Satellite, color: 'text-amber-500',   route: '/dashboard/dumpers/trucks' },
  { key: 'dumper_driver_pay',  icon: Wallet,    color: 'text-amber-500',   route: '/dashboard/dumpers/driver-pay' },
];

const FLATBED_OPS: ModuleDef[] = [
  { key: 'flatbed_trips',       icon: Container,     color: 'text-blue-500',    route: '/dashboard/flatbed' },
  { key: 'flatbed_estimate',    icon: BarChart3,     color: 'text-emerald-500', route: '/dashboard/flatbed/estimate' },
  { key: 'cash_flow',           icon: Banknote,      color: 'text-emerald-500', route: '/dashboard/flatbed/cash-flow' },
  { key: 'overdue_collections', icon: AlertTriangle, color: 'text-rose-500',    route: '/dashboard/cashflow/overdue' },
  { key: 'flatbed_driver_pay',  icon: UserCog,       color: 'text-indigo-500',  route: '/dashboard/flatbed/driver-pay' },
];

const SCOPE: Record<string, string> = {
  'PRJ-NEOM': 'neom', 'PRJ-RIYADH': 'riyadh', 'PRJ-DUMPERS': 'dumpers',
  'PRJ-RIYADH-WATER': 'riyadh_water',
};
const OPS: Record<string, ModuleDef[]> = {
  neom: NEOM_OPS, riyadh: FLATBED_OPS, dumpers: DUMPERS_OPS,
  // Same work as NEOM, another city: the same modules, its own data.
  riyadh_water: NEOM_OPS,
};

function ProjectSummary({ scope }: { scope: string }) {
  const { data } = useQuery({
    queryKey: ['project-summary', scope],
    queryFn: async () => (await apiClient.get<{ items: Array<{ label: string; value: string; tone?: string }> }>(
      '/api/v1/project-reports/summary', { params: { scope } })).data.items,
  });
  if (!data) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-10">
      {data.map((s, i) => (
        <div key={i} className="rounded-xl border border-border bg-card p-4">
          <div className="text-xs text-muted-foreground">{s.label}</div>
          <div className={`text-xl font-semibold mt-1 ${s.tone === 'warn' ? 'text-rose-600' : ''}`}>{s.value}</div>
        </div>
      ))}
    </div>
  );
}

export default function DashboardPage() {
  const { user, logout, loadCurrentUser } = useAuth();
  if (user?.role === 'driver') return <DriverSelfView />;
  if (user?.role === 'investor') return <InvestorSelfView />;
  if (user?.role === 'construction_supervisor') return <SupervisorHomePage />;

  const { t } = useTranslation();
  const { project } = useCurrentProject();
  const scope = project?.code ? SCOPE[project.code] : null;

  useEffect(() => { if (!user) loadCurrentUser(); }, [user, loadCurrentUser]);

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10 backdrop-blur-sm bg-card/80">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={36} variant="mark" />
            <div className="hidden sm:block">
              <div className="font-display text-lg leading-none text-gradient-gold">JASR HUB</div>
              <div className="text-[11px] text-muted-foreground">{t('app.tagline')}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/home" className="flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs hover:border-primary/60 transition" title={t('project.switch', { defaultValue: 'Switch project' })}>
              <span className={`h-2 w-2 rounded-full ${project ? 'bg-emerald-500' : 'bg-muted-foreground'}`} />
              <span className="font-medium">{project ? project.nameEn : t('project.allProjects', { defaultValue: 'All projects' })}</span>
            </Link>
            <Link to="/dashboard/settings"><Button variant="ghost" size="sm" className="gap-2"><Settings className="h-4 w-4" /><span className="hidden md:inline">Settings</span></Button></Link>
            <MaintenanceCounter />
            <LanguageSwitcher />
            <ThemeToggle />
            <div className="ms-2 ps-2 border-s border-border">
              <Button variant="ghost" size="sm" onClick={logout} className="gap-2">
                <LogOut className="h-4 w-4" /><span className="hidden md:inline">{t('auth.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-10">
        <h1 className="font-display text-2xl md:text-3xl text-foreground tracking-tight mb-8">
          {project ? project.nameEn : t('project.allProjects', { defaultValue: 'All projects' })}
        </h1>

        {scope ? (
          <>
            <ProjectSummary scope={scope} />
            <ModuleGroup groupLabel="daily" modules={OPS[scope]} />
            <ModuleGroup groupLabel="fleet" modules={COMMON_FLEET} />
          </>
        ) : (
          <div className="rounded-2xl border border-border bg-card p-8 text-center">
            <div className="text-lg font-medium">Pick a project to work in</div>
            <div className="text-sm text-muted-foreground mt-1 mb-5">Each project shows only its own daily cards. Or open the cross-project owner view.</div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/home"><Button>Choose a project</Button></Link>
              <Link to="/dashboard/owner"><Button variant="outline" className="gap-2"><BarChart3 className="h-4 w-4" /> Owner view</Button></Link>
              <Link to="/dashboard/settings"><Button variant="outline" className="gap-2"><Settings className="h-4 w-4" /> Settings</Button></Link>
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        © Vida Group · Saudi Arabia
      </footer>
    </div>
  );
}
